import { Form, Modal, Radio, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useRemapQuestion } from '../../../api/Questions';
import ThemePicker from '../../../components/ThemePicker';
import { RecodeOption } from '../../../constants';
import { themeType } from '../../../types';

const { Text, Paragraph } = Typography;

const StyledModal = styled(Modal)`
  .ant-modal-body {
    overflow-x: scroll;
    max-height: calc(100vh - 250px);
  }
`;

const StyledRadioGroup = styled(Radio.Group)`
  width: 100%;
`;

const StyledFormItem = styled(Form.Item)`
  padding: 0 8px;
`;

function RecodeModal({
  visible,
  setVisible,
  themes,
  questionId,
  surveyId,
  onStart,
  recodesRemaining,
}) {
  const [form] = Form.useForm();
  const recodeOption = Form.useWatch('option', form);

  const { mutate: remapQuestion, isLoading: remapQuestionLoading } = useRemapQuestion();

  const themeHierarchy = useMemo(
    () =>
      themes.reduce((acc, theme) => {
        if (theme.is_parent) {
          const children = themes
            .filter((t) => t.parent_theme_id === theme.id)
            .map((t) => ({ data: t }));
          acc.push({
            data: theme,
            children,
          });
        } else if (!theme.parent_theme_id) {
          acc.push({
            data: theme,
            children: [],
          });
        }
        return acc;
      }, []),
    [themes],
  );

  const handleRemapQuestion = (option, themeIds) => {
    remapQuestion(
      { questionId, surveyId, data: { option, theme_ids: themeIds } },
      {
        onSuccess: () => {
          onStart();
          setVisible(false);
        },
      },
    );
  };

  const onFinish = async (formValues) => {
    const themeIds = themes.map((t) => t.id).filter((id) => !formValues.themes?.includes(id));
    handleRemapQuestion(formValues.option, themeIds);
    setVisible(false);
  };

  return (
    <StyledModal
      title="Recode"
      open={visible}
      onOk={() => {
        form
          .validateFields()
          .then(onFinish)
          .catch(() => {});
      }}
      okText="Recode selected themes"
      okButtonProps={{ disabled: !recodeOption || recodesRemaining === 0 }}
      onCancel={() => setVisible(false)}
      confirmLoading={remapQuestionLoading}
      closable={false}
      maskClosable={false}
      destroyOnClose
    >
      <Form form={form} layout="vertical" preserve={false} colon={false}>
        <Paragraph>Recode to update tagging of responses to themes.</Paragraph>
        <Form.Item label={<Text strong>Choose what to update</Text>} name="option">
          <StyledRadioGroup
            options={[
              {
                value: RecodeOption.UNCODED,
                label: (
                  <Text>
                    <Text strong>New themes: </Text> code new themes with no responses currently
                    tagged to them.
                  </Text>
                ),
              },
              {
                value: RecodeOption.ALL,
                label: (
                  <Text>
                    <Text strong>All themes: </Text> recode the entire data, overwriting any manual
                    retags you have done.
                  </Text>
                ),
              },
              {
                value: RecodeOption.CUSTOM,
                label: (
                  <Text>
                    <Text strong>Custom selection: </Text> select themes to update (best if you
                    tagged a few responses to new themes or made changes to existing themes).
                  </Text>
                ),
              },
            ]}
          />
        </Form.Item>
        {recodeOption === RecodeOption.CUSTOM && (
          <StyledFormItem
            name="themes"
            valuePropName="deselectedThemes"
            initialValue={themes.map((t) => t.id)}
          >
            <ThemePicker
              themes={themeHierarchy}
              switchTooltip="Select themes to include in recoding"
              startSelected={false}
            />
          </StyledFormItem>
        )}
        <Paragraph>
          Fathom always reprocesses all responses to ensure accuracy, but only revises tagging based
          on your selection above. Note that if you recode “All Themes”, any manual changes you have
          made to tagging may be overwritten and lost.
        </Paragraph>
        <Text strong>Recodes remaining: {recodesRemaining}</Text>
      </Form>
    </StyledModal>
  );
}

RecodeModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  themes: PropTypes.arrayOf(themeType).isRequired,
  questionId: PropTypes.number.isRequired,
  surveyId: PropTypes.number.isRequired,
  onStart: PropTypes.func.isRequired,
  recodesRemaining: PropTypes.number.isRequired,
};

export default RecodeModal;
